/**
 * Anillo de progreso. Sustituye a las barritas en toda la página: el arco
 * comunica "cuánto llevas completado" mejor que una barra cuando el número va
 * dentro o al lado.
 *
 * Lo que va dentro se pasa como children y se centra: el número en el hero y en
 * cada métrica, el icono del factor en las pestañas.
 *
 * @param {object}  props
 * @param {number}  props.value
 * @param {number}  [props.max=100]
 * @param {string}  props.color   color del arco (hex, no clase de Tailwind)
 * @param {number}  [props.size=56]    lado en px
 * @param {number}  [props.stroke=4]   grosor del trazo en px
 * @param {string}  [props.track]      color de la pista de fondo
 * @param {boolean} [props.dashed]     pista a rayas y sin arco, para las
 *                                     métricas neutralizadas: un anillo al 0%
 *                                     diría "lo tienes mal" y no hay nada medido
 * @param {string}  [props.label]      si se pasa, el anillo se anuncia como
 *                                     `progressbar` con ese nombre accesible
 * @param {string}  [props.className]
 */
export default function ScoreRing({
  value,
  max = 100,
  color,
  size = 56,
  stroke = 4,
  track = '#e2e8f0',
  dashed = false,
  label,
  className = '',
  children,
}) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const mid = size / 2;

  const a11y = label && !dashed
    ? {
      role: 'progressbar',
      'aria-label': label,
      'aria-valuemin': 0,
      'aria-valuemax': max,
      'aria-valuenow': value,
    }
    : {};

  return (
    <div
      {...a11y}
      className={`relative shrink-0 ${className}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={mid}
          cy={mid}
          r={r}
          fill="none"
          stroke={track}
          strokeWidth={stroke}
          strokeDasharray={dashed ? '3 5' : undefined}
          strokeLinecap={dashed ? 'round' : undefined}
        />
        {!dashed && (
          <circle
            cx={mid}
            cy={mid}
            r={r}
            fill="none"
            stroke={color}
            strokeWidth={stroke}
            strokeDasharray={circ}
            strokeDashoffset={circ - (pct / 100) * circ}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 1s ease' }}
          />
        )}
      </svg>
      <div className="absolute inset-0 grid place-items-center">{children}</div>
    </div>
  );
}
